import { Button, Flex, Space } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import styles from "~components/AddPersonRolesModal/AddPersonRolesModal.module.css";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import type { AddPlayerToRosterTableRow } from "~pages/Roster/components/AddPlayerToRoster/types/AddPlayerToRosterTableRow";
import ImageView from "../../../../../components/ImageView/ImageView.tsx";

const AddButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => {
  return (
    <Button
      type="default"
      size="small"
      icon={<i className="fas fa-plus" />}
      onClick={onClick}
      className={styles.addButton}
      disabled={disabled}
    />
  );
};

export const useGetAddPersonToRosterColumns = (
  isLoading: boolean,
  handleAdd: (row: AddPlayerToRosterTableRow) => void,
  isAddAllowed = true,
  roster: Array<any>,
  fixtureId?: string,
) => {
  const { formatMessage } = useIntl();

  const columns: ColumnType<AddPlayerToRosterTableRow>[] = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (value: string, row: AddPlayerToRosterTableRow) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: "nationality",
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
    },
    {
      title: formatMessage({ id: "date.of.birth" }),
      dataIndex: "dob",
      render: (value: string) => <>{value ? dayjs(value).format("DD/MM/YYYY") : ""}</>,
    },
    ...(fixtureId
      ? [
          {
            title: formatMessage({ id: "roster.number" }),
            dataIndex: "bib",
            width: 20,
          },
          {
            title: formatMessage({ id: "position" }),
            dataIndex: "position",
            width: 20,
          },
          {
            title: formatMessage({ id: "roster.captain" }),
            dataIndex: "isCaptain",
            width: 20,
            render: (value: boolean) => <i className={value ? "far fa-check-square" : "far fa-square"} />,
          },
        ]
      : []),
    {
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
    },
    {
      align: "right",
      render: (_, record: AddPlayerToRosterTableRow) => {
        const existingRosterEntry = roster.find((r) => r.personId === record.personId);
        if (existingRosterEntry) {
          return (
            <Flex justify="center" align="center" style={{ marginLeft: "auto", width: 24 }}>
              <i className="fas fa-check" />
            </Flex>
          );
        }
        return <AddButton disabled={isLoading || !isAddAllowed} onClick={() => handleAdd(record)} />;
      },
    },
  ];

  return columns;
};
